import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Button from "../../../../../components/Button";
import Spinner from "../../../../../components/Spinner";
import Toast from "../../../../../components/Toast";
import Modal from "../../../../../components/Modal";
import ManagerStatementService from "../../../../../services/managerStatement.service";
import { InvoiceDocumentApi } from "../../../../../interfaces/managerStatement.interface";

interface ManagerInvoiceDocumentProps {
  statementId: string;
  isInvoiceLocked: boolean;
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024;

const ManagerInvoiceDocument: React.FC<ManagerInvoiceDocumentProps> = ({ statementId, isInvoiceLocked }) => {
  const { t } = useTranslation();
  const base = "reconciliation.managerStatement.detail.pageTexts.documentsTab.managerInvoice";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canModify = !isInvoiceLocked;

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [invoiceDocument, setInvoiceDocument] = useState<InvoiceDocumentApi | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isMobileOrTablet = /Mobi|Android|Tablet|iPad/i.test(navigator.userAgent);

  const loadDocument = useCallback(() => {
    setLoading(true);
    ManagerStatementService.getInvoiceDocument(statementId)
      .then((response: any) => {
        setInvoiceDocument(response ?? null);
        setLoading(false);
      })
      .catch(() => {
        setInvoiceDocument(null);
        setLoading(false);
      });
  }, [statementId]);

  useEffect(() => {
    if (!statementId) return;
    loadDocument();
  }, [statementId, loadDocument]);

  useEffect(() => {
    if (loading) return;

    const base64 = invoiceDocument?.pdf_base64;

    if (!base64) {
      setPdfUrl(null);
      return;
    }

    let objectUrl: string | null = null;

    const loadPdf = async () => {
      try {
        const blob = await fetch(`data:application/pdf;base64,${base64}`).then((res) => res.blob());
        // Wrap in a File so the browser saves it under the generated name when downloaded.
        const file = new File([blob], invoiceDocument?.stored_file_name || "manager-invoice.pdf", { type: "application/pdf" });
        objectUrl = URL.createObjectURL(file);
        setPdfUrl(objectUrl);
      } catch (error) {
        setPdfUrl(null);
      }
    };

    loadPdf();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceDocument, loading]);

  const uploadFile = useCallback((file: File) => {
    if (file.type !== "application/pdf") {
      toast.error(<Toast message={t(`${base}.fileTypeError`)} />, { icon: false });
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(<Toast message={t(`${base}.fileSizeError`)} />, { icon: false });
      return;
    }

    setUploading(true);
    ManagerStatementService.uploadInvoiceDocument(statementId, file)
      .then(() => {
        toast.success(<Toast message={t(`${base}.uploadSuccess`)} />, { icon: false });
        setUploading(false);
        loadDocument();
      })
      .catch(() => {
        setUploading(false);
      });
  }, [statementId, loadDocument, t, base]);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDownload = () => {
    if (!pdfUrl) return;

    const link = window.document.createElement("a");
    link.href = pdfUrl;
    link.download = invoiceDocument?.stored_file_name || "manager-invoice.pdf";
    link.click();
  };

  const handleDeleteConfirm = () => {
    setDeleting(true);
    ManagerStatementService.deleteInvoiceDocument(statementId)
      .then(() => {
        toast.success(<Toast message={t(`${base}.deleteSuccess`)} />, { icon: false });
        setDeleting(false);
        setDeleteModalOpen(false);
        loadDocument();
      })
      .catch(() => {
        setDeleting(false);
      });
  };

  if (loading) {
    return (
      <div className="m-6">
        <div className="card bg-white">
          <div className="card-body text-center">
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="m-6">
      <div className="card bg-white">
        {!invoiceDocument?.has_document ? (
          <div className="card-body">
            {canModify ? (
              <div
                className="border border-dashed rounded p-6 text-center cursor-pointer"
                style={{ borderColor: "var(--bs-border-color)" }}
                role="button"
                tabIndex={0}
                aria-label={t(`${base}.uploadPrompt`)}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !uploading) {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                data-testid="manager-invoice-dropzone"
              >
                {uploading ? (
                  <Spinner />
                ) : (
                  <>
                    <i className="ri-upload-cloud-2-line fs-3 text-muted d-block mb-1"></i>
                    <span className="text-muted small">{t(`${base}.uploadPrompt`)}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="text-muted small text-center" data-testid="manager-invoice-no-document-locked">
                {t(`${base}.noDocumentLocked`)}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="fw-semibold">{invoiceDocument.stored_file_name}</div>
                <div className="text-muted small">
                  {formatFileSize(invoiceDocument.file_size)}
                  {invoiceDocument.uploaded_at
                    ? ` · ${t(`${base}.toolbar.uploadedOn`, { date: invoiceDocument.uploaded_at })}`
                    : ""}
                </div>
              </div>
              <div className="d-flex gap-2">
                {canModify && (
                  <Button
                    className="btn btn-outline-secondary"
                    text={t(`${base}.reuploadButton`)}
                    loading={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  />
                )}
                {canModify && (
                  <Button
                    className="btn btn-outline-danger"
                    text={t(`${base}.deleteButton`)}
                    onClick={() => setDeleteModalOpen(true)}
                  />
                )}
                <Button
                  className="btn btn-primary"
                  text={t(`${base}.downloadButton`)}
                  onClick={handleDownload}
                />
              </div>
            </div>
            <div className="card-body p-0">
              {isMobileOrTablet ? (
                invoiceDocument.file_url && (
                  <Button
                    className="btn btn-primary m-3"
                    text={t(`${base}.openPdf`)}
                    onClick={() => window.open(invoiceDocument.file_url, "_blank")}
                  />
                )
              ) : (
                invoiceDocument.file_url && (
                  <embed
                    src={`${invoiceDocument.file_url}#navpanes=0&zoom=100`}
                    type="application/pdf"
                    width="100%"
                    height="600px"
                  />
                )
              )}
            </div>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="d-none"
        onChange={handleFileSelected}
      />

      <Modal
        open={deleteModalOpen}
        maxWidth="sm"
        title={t(`${base}.deleteConfirm.title`)}
        content={t(`${base}.deleteConfirm.message`)}
        action={(
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t(`${base}.deleteConfirm.cancelBtn`)}
              disabled={deleting}
              onClick={() => setDeleteModalOpen(false)}
            />
            <Button
              className="btn btn-danger"
              text={t(`${base}.deleteConfirm.confirmBtn`)}
              loading={deleting}
              onClick={handleDeleteConfirm}
            />
          </>
        )}
        onClose={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default ManagerInvoiceDocument;
